/**
 * TimePicker - Improved time selector with scroll wheel, visual clock, and fine minute control
 * Fixed: AM/PM conversion feedback loop that caused 0800→0600 bug
 */
import React, { useState, useEffect, useRef, useCallback } from 'react';
import { Clock, ChevronUp, ChevronDown } from 'lucide-react';
import { cn } from '@/lib/utils';

const SpinnerColumn = ({ value, options, onChange, label, width = 'w-16' }) => {
  const containerRef = useRef(null);
  const currentIdxRef = useRef(options.indexOf(value));

  // Keep ref in sync
  currentIdxRef.current = options.indexOf(value);

  const handleWheel = useCallback((e) => {
    e.preventDefault();
    const idx = currentIdxRef.current;
    if (idx === -1) return;
    if (e.deltaY > 0) {
      onChange(options[(idx + 1) % options.length]);
    } else {
      onChange(options[(idx - 1 + options.length) % options.length]);
    }
  }, [options, onChange]);

  useEffect(() => {
    const el = containerRef.current;
    if (!el) return;
    el.addEventListener('wheel', handleWheel, { passive: false });
    return () => el.removeEventListener('wheel', handleWheel);
  }, [handleWheel]);

  const currentIdx = options.indexOf(value);
  const increment = () => {
    const next = (currentIdx + 1) % options.length;
    onChange(options[next]);
  };
  const decrement = () => {
    const prev = (currentIdx - 1 + options.length) % options.length;
    onChange(options[prev]);
  };

  return (
    <div ref={containerRef} className={cn("flex flex-col items-center select-none", width)}>
      <button
        type="button"
        onClick={decrement}
        className="p-0.5 rounded hover:bg-slate-100 text-slate-400 hover:text-slate-700 transition-colors"
        aria-label={`Previous ${label}`}
      >
        <ChevronUp className="h-4 w-4" />
      </button>
      <div className="text-lg font-semibold tabular-nums py-0.5 text-slate-800">
        {value}
      </div>
      <button
        type="button"
        onClick={increment}
        className="p-0.5 rounded hover:bg-slate-100 text-slate-400 hover:text-slate-700 transition-colors"
        aria-label={`Next ${label}`}
      >
        <ChevronDown className="h-4 w-4" />
      </button>
    </div>
  );
};

const TimePicker = ({ value, onChange, label, className = '', minuteStep = 5 }) => {
  const [hour, setHour] = useState('12');
  const [minute, setMinute] = useState('00');
  const [period, setPeriod] = useState('AM');
  const isInternalChange = useRef(false);

  // Generate minute options based on step
  const minuteOptions = Array.from(
    { length: Math.floor(60 / minuteStep) },
    (_, i) => String(i * minuteStep).padStart(2, '0')
  );
  const hourOptions = Array.from({ length: 12 }, (_, i) => String(i + 1).padStart(2, '0'));

  // Parse incoming value - only when changed externally (not from our own emit)
  useEffect(() => {
    if (isInternalChange.current) {
      isInternalChange.current = false;
      return;
    }
    if (!value) return;
    const parts = value.split(':');
    if (parts.length < 2) return;
    let h = parseInt(parts[0], 10);
    const m = parts[1].slice(0, 2);
    if (isNaN(h)) return;

    let newPeriod = 'AM';
    let newHour = '12';

    if (h === 0) { newHour = '12'; newPeriod = 'AM'; }
    else if (h === 12) { newHour = '12'; newPeriod = 'PM'; }
    else if (h > 12) { newHour = String(h - 12).padStart(2, '0'); newPeriod = 'PM'; }
    else { newHour = String(h).padStart(2, '0'); newPeriod = 'AM'; }

    // Snap minute to nearest step
    const mInt = parseInt(m, 10);
    const snapped = String(Math.round(mInt / minuteStep) * minuteStep % 60).padStart(2, '0');

    setHour(newHour);
    setMinute(snapped);
    setPeriod(newPeriod);
  }, [value, minuteStep]);

  const emit = useCallback((h, m, p) => {
    let hr = parseInt(h, 10);
    if (isNaN(hr)) return;
    if (p === 'AM' && hr === 12) hr = 0;
    else if (p === 'PM' && hr !== 12) hr += 12;
    const val = `${String(hr).padStart(2, '0')}:${m}`;
    isInternalChange.current = true;
    onChange(val);
  }, [onChange]);

  return (
    <div className={className}>
      {label && <label className="block text-sm font-medium text-slate-700 mb-1.5">{label}</label>}
      <div className="inline-flex items-center gap-0 border border-slate-200 rounded-lg bg-white px-2 py-1 shadow-sm hover:border-slate-300 transition-colors">
        <Clock className="h-3.5 w-3.5 text-slate-400 mr-1.5 flex-shrink-0" />
        <SpinnerColumn
          value={hour}
          options={hourOptions}
          onChange={(v) => { setHour(v); emit(v, minute, period); }}
          label="hour"
          width="w-10"
        />
        <span className="text-slate-300 font-bold text-lg leading-none pb-0.5">:</span>
        <SpinnerColumn
          value={minute}
          options={minuteOptions}
          onChange={(v) => { setMinute(v); emit(hour, v, period); }}
          label="minute"
          width="w-10"
        />
        <div className="ml-1 border-l border-slate-200 pl-1">
          <SpinnerColumn
            value={period}
            options={['AM', 'PM']}
            onChange={(v) => { setPeriod(v); emit(hour, minute, v); }}
            label="period"
            width="w-10"
          />
        </div>
      </div>
    </div>
  );
};

/**
 * DateTimePicker - Combined date + time selector
 */
const DateTimePicker = ({ value, onChange, label, className = '' }) => {
  const [date, setDate] = useState('');
  const [time, setTime] = useState('09:00');

  useEffect(() => {
    if (!value) return;
    const dt = value.includes('T') ? value : value;
    const parts = dt.split('T');
    if (parts[0]) setDate(parts[0]);
    if (parts[1]) setTime(parts[1].slice(0, 5));
  }, [value]);

  const emit = (d, t) => {
    if (d && t) onChange(`${d}T${t}`);
    else if (d) onChange(`${d}T${time}`);
  };

  return (
    <div className={className}>
      {label && <label className="block text-sm font-medium text-slate-700 mb-1.5">{label}</label>}
      <div className="flex flex-col sm:flex-row gap-2 items-start sm:items-end">
        <div className="w-full sm:flex-1">
          <input
            type="date"
            value={date}
            onChange={e => { setDate(e.target.value); emit(e.target.value, time); }}
            className="w-full h-10 border border-slate-200 rounded-lg px-3 text-sm bg-white focus:outline-none focus:ring-2 focus:ring-emerald-500 shadow-sm"
          />
        </div>
        <TimePicker
          value={time}
          onChange={t => { setTime(t); emit(date, t); }}
        />
      </div>
    </div>
  );
};

export { TimePicker, DateTimePicker };
export default TimePicker;
